import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Progress,
  Icon,
  Divider,
  Button,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FaHome,
  FaEnvelope,
  FaPhone,
  FaChevronDown,
  FaChevronUp,
  FaLandmark,
} from 'react-icons/fa';
import { NavbarWithCallToAction } from '../../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { SEOHead } from '../../../../components/SEOHead';
import { PhaseTaskTimeline } from './components/PhaseTaskTimeline';
import { DocumentRequestCard } from './components/DocumentRequestCard';
import { ScheduledCallCard } from './components/ScheduledCallCard';
import { SMSFBadge } from './components/SMSFBadge';
import {
  STUB_DEAL,
  STUB_PHASE2_TASKS,
  STUB_PHASE2_DOCS,
  STUB_PHASE2_CALLS,
} from './stubs';
import type { PortalDocument, PortalDeal } from './types';

const PROPERTY_PRIMARY = '#0D7F6E';
const NAVY = '#141C49';

// Progress: count complete tasks / total client-facing tasks
const calcProgress = (tasks: typeof STUB_PHASE2_TASKS) => {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === 'COMPLETE').length;
  return total === 0 ? 0 : Math.round((done / total) * 100);
};

const PortalPhase2Finance: React.FC = () => {
  const [deal] = useState<PortalDeal>(STUB_DEAL);
  const [tasks] = useState(STUB_PHASE2_TASKS);
  const [docs, setDocs] = useState<PortalDocument[]>(STUB_PHASE2_DOCS);
  const [calls] = useState(STUB_PHASE2_CALLS);
  const { isOpen: smsfOpen, onToggle: toggleSmsf } = useDisclosure({ defaultIsOpen: true });

  const progress = calcProgress(tasks);
  const visibleDocs = docs.filter(d => !d.isSMSFOnly || deal.isSMSF);

  const handleDocUpload = (documentId: string, fileUrl: string) => {
    setDocs(prev =>
      prev.map(d => d.id === documentId ? { ...d, status: 'UPLOADED' as const, fileUrl } : d)
    );
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/property/portal/phase2"
        fallbackTitle="Phase 2 — Finance & Purchase | One Property Australia"
        fallbackDescription="Track your finance and purchase progress with One Property Australia"
      />
      <NavbarWithCallToAction />

      {/* Hero strip */}
      <Box bg={NAVY} color="white" py={{ base: 10, md: 14 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} align="flex-start">
            <HStack spacing={3} flexWrap="wrap">
              <Badge
                bg={PROPERTY_PRIMARY}
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Phase 2 of 5
              </Badge>
              <SMSFBadge show={deal.isSMSF} />
            </HStack>
            <Heading size={{ base: 'xl', md: '2xl' }} fontWeight="black">
              Finance & Purchase
            </Heading>
            <Text color="gray.300" maxW="xl" fontSize="md">
              Your broker and solicitors are working through the finance and contract process.
              Track every step here and upload documents as they're requested.
            </Text>

            {/* Progress bar */}
            <Box w="full" maxW="md" mt={2}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.300">Phase progress</Text>
                <Text fontSize="sm" fontWeight="bold" color="white">{progress}%</Text>
              </HStack>
              <Progress
                value={progress}
                size="md"
                borderRadius="full"
                bg="whiteAlpha.200"
                sx={{ '& > div': { background: PROPERTY_PRIMARY } }}
              />
            </Box>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        {/* Main two-column grid */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={10}>
          {/* Left: task timeline */}
          <Box>
            <Heading size="md" color={NAVY} mb={5}>
              Your Milestones
            </Heading>
            <Box
              bg="white"
              borderRadius="2xl"
              p={6}
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.100"
            >
              <PhaseTaskTimeline tasks={tasks} />
            </Box>
          </Box>

          {/* Right: team + calls */}
          <VStack spacing={6} align="stretch">
            {/* Your Team */}
            <Box>
              <Heading size="md" color={NAVY} mb={5}>
                Your Team
              </Heading>
              <VStack spacing={3} align="stretch">
                <TeamCard
                  name={deal.assignedStrategist.name}
                  role={deal.assignedStrategist.role}
                  email={deal.assignedStrategist.email}
                  phone={deal.assignedStrategist.phone}
                  bookingUrl={deal.assignedStrategist.bookingUrl}
                  accentColor={PROPERTY_PRIMARY}
                />
                <TeamCard
                  name={deal.assignedBroker.name}
                  role={deal.assignedBroker.role}
                  email={deal.assignedBroker.email}
                  phone={deal.assignedBroker.phone}
                  bookingUrl={deal.assignedBroker.bookingUrl}
                  accentColor="#1A56DB"
                />
                {deal.arrowwhiteContact && (
                  <TeamCard
                    name={deal.arrowwhiteContact.name}
                    role={deal.arrowwhiteContact.role}
                    email={deal.arrowwhiteContact.email}
                    phone={deal.arrowwhiteContact.phone}
                    accentColor="#6B46C1"
                  />
                )}
              </VStack>
            </Box>

            {/* Upcoming Calls */}
            <Box>
              <Heading size="md" color={NAVY} mb={5}>
                Upcoming Calls
              </Heading>
              {calls.length === 0 ? (
                <Box
                  bg="white"
                  borderRadius="xl"
                  p={5}
                  textAlign="center"
                  border="1px dashed"
                  borderColor="gray.300"
                >
                  <Text color="gray.400" fontSize="sm">
                    No calls scheduled yet — your team will be in touch soon
                  </Text>
                </Box>
              ) : (
                <VStack spacing={3} align="stretch">
                  {calls.map(call => (
                    <ScheduledCallCard key={call.id} call={call} />
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </SimpleGrid>

        <Divider mb={10} />

        {/* Documents panel */}
        <Box mb={10}>
          <HStack justify="space-between" mb={5} flexWrap="wrap" spacing={3}>
            <Heading size="md" color={NAVY}>
              Documents
            </Heading>
            <Badge
              colorScheme={visibleDocs.filter(d => d.status === 'REQUESTED').length > 0 ? 'orange' : 'green'}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
            >
              {visibleDocs.filter(d => d.status === 'REQUESTED' || d.status === 'UPLOADED').length} pending ·{' '}
              {visibleDocs.filter(d => d.status === 'APPROVED').length} approved
            </Badge>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {visibleDocs.map(doc => (
              <DocumentRequestCard key={doc.id} doc={doc} onUpload={handleDocUpload} />
            ))}
          </SimpleGrid>
        </Box>

        {/* SMSF branch section */}
        {deal.isSMSF && (
          <Box
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            borderRadius="2xl"
            p={6}
            mb={10}
          >
            <HStack justify="space-between" cursor="pointer" onClick={toggleSmsf}>
              <HStack spacing={3}>
                <Icon as={FaLandmark} color="orange.600" boxSize={5} />
                <Heading size="sm" color="orange.800">
                  SMSF — Additional Requirements
                </Heading>
              </HStack>
              <Icon as={smsfOpen ? FaChevronUp : FaChevronDown} color="orange.400" />
            </HStack>
            <Collapse in={smsfOpen}>
              <VStack align="stretch" spacing={3} mt={4}>
                <Text fontSize="sm" color="orange.800">
                  Because your property is being purchased through your Self-Managed Super Fund,
                  there are a few additional steps and documents required.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
                  {docs.filter(d => d.isSMSFOnly).map(doc => (
                    <DocumentRequestCard key={doc.id} doc={doc} onUpload={handleDocUpload} />
                  ))}
                </SimpleGrid>
                <Box
                  bg="orange.100"
                  borderRadius="lg"
                  p={3}
                >
                  <Text fontSize="xs" color="orange.700">
                    <strong>Statement of Credit Advice:</strong> Your broker will prepare this
                    document as required for your SMSF loan application. A copy will be uploaded
                    to your portal when complete.
                  </Text>
                </Box>
              </VStack>
            </Collapse>
          </Box>
        )}
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

// Inline team member card — small enough not to warrant its own file
interface TeamCardProps {
  name: string;
  role: string;
  email?: string;
  phone?: string;
  bookingUrl?: string;
  accentColor: string;
}

const TeamCard: React.FC<TeamCardProps> = ({ name, role, email, phone, bookingUrl, accentColor }) => (
  <Box
    bg="white"
    borderRadius="xl"
    p={4}
    boxShadow="sm"
    border="1px solid"
    borderColor="gray.100"
    position="relative"
    overflow="hidden"
    _before={{
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      w: '4px',
      h: '100%',
      bg: accentColor,
      borderRadius: '4px 0 0 4px',
    }}
  >
    <VStack align="flex-start" spacing={2}>
      <VStack align="flex-start" spacing={0}>
        <Text fontWeight="bold" fontSize="sm" color={NAVY}>{name}</Text>
        <Text fontSize="xs" color="gray.500">{role}</Text>
      </VStack>
      <VStack align="flex-start" spacing={1}>
        {email && (
          <HStack spacing={2} color="gray.500">
            <Icon as={FaEnvelope} boxSize={3} />
            <Text fontSize="xs">{email}</Text>
          </HStack>
        )}
        {phone && (
          <HStack spacing={2} color="gray.500">
            <Icon as={FaPhone} boxSize={3} />
            <Text fontSize="xs">{phone}</Text>
          </HStack>
        )}
      </VStack>
      {bookingUrl && (
        <Button
          as="a"
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          variant="outline"
          borderColor={accentColor}
          color={accentColor}
          _hover={{ bg: `${accentColor}15` }}
          mt={1}
        >
          Book a Call
        </Button>
      )}
    </VStack>
  </Box>
);

export default PortalPhase2Finance;
